import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  Eye,
  Heart,
  Loader2,
  AlertCircle,
} from 'lucide-react';

/* ─── Types ─── */

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  youtube: '#FF0000',
  pinterest: '#E60023',
  threads: '#000000',
};

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  threads: 'Threads',
};

/* ─── API Hook ─── */
const useSharedReportAPI = (token: string) => {
  const baseUrl = import.meta.env.VITE_API_URL || '';

  return {
    fetchReport: async () => {
      const response = await fetch(`${baseUrl}/api/reports/shared/${token}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error('Report not found');
        if (response.status === 410) throw new Error('Report has expired');
        throw new Error('Failed to load report');
      }
      return response.json();
    },
  };
};

/* ─── Format Helpers ─── */
const formatNumber = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/* ─── Stat Card Component ─── */
interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
          {value}
        </p>
      </div>
      <div className="text-blue-600 dark:text-blue-400">{icon}</div>
    </div>
  </div>
);

/* ─── Main Component ─── */
const SharedReportPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const api = useSharedReportAPI(token || '');
  const { data: report, isLoading, error } = useQuery({
    queryKey: ['shared-report', token],
    queryFn: api.fetchReport,
    enabled: !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('common_loading', 'Loading report...')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-red-200 dark:border-red-800 p-8 max-w-md text-center">
          <AlertCircle size={32} className="mx-auto text-red-600 mb-4" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t('reports_not_found', 'Report Not Found')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {error instanceof Error && error.message === 'Report has expired'
              ? t(
                  'reports_expired',
                  'This report has expired and is no longer available.'
                )
              : t(
                  'reports_not_found_message',
                  'The report you are looking for does not exist or has been deleted.'
                )}
          </p>
        </div>
      </div>
    );
  }

  const metrics = report.metrics_snapshot;
  const platformData = Object.entries(metrics.platform_distribution).map(
    ([name, count]) => ({
      name: PLATFORM_LABELS[name] || name,
      value: count as number,
      color: PLATFORM_COLORS[name] || '#6b7280',
    })
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {report.branding?.logo_url && (
            <img
              src={report.branding.logo_url}
              alt={report.branding.company_name || 'Company Logo'}
              className="h-8 mb-4"
            />
          )}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {report.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {formatDate(report.date_from)} {t('common_to', 'to')}{' '}
            {formatDate(report.date_to)}
          </p>
          {report.branding?.company_name && (
            <p className="text-gray-700 dark:text-gray-300 mt-2">
              {report.branding.company_name}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            label={t('reports_total_posts', 'Total Posts')}
            value={String(metrics.total_posts)}
            icon={<TrendingUp size={24} />}
          />
          <StatCard
            label={t('reports_total_reach', 'Total Reach')}
            value={formatNumber(metrics.total_reach)}
            icon={<Eye size={24} />}
          />
          <StatCard
            label={t('reports_total_engagement', 'Total Engagement')}
            value={formatNumber(metrics.total_engagement)}
            icon={<Heart size={24} />}
          />
          <StatCard
            label={t('reports_engagement_rate', 'Engagement Rate')}
            value={`${metrics.engagement_rate}%`}
            icon={<TrendingUp size={24} />}
          />
        </div>

        {/* Engagement Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('reports_likes', 'Likes')}
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatNumber(metrics.total_likes)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('reports_comments', 'Comments')}
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatNumber(metrics.total_comments)}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('reports_shares', 'Shares')}
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {formatNumber(metrics.total_shares)}
            </p>
          </div>
        </div>

        {/* Platform Distribution */}
        {platformData.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('reports_platform_distribution', 'Platform Distribution')}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {platformData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Content Type Performance */}
        {metrics.content_type_performance.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('reports_content_performance', 'Content Performance by Type')}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={metrics.content_type_performance}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="avg_engagement"
                  fill="#3b82f6"
                  name={t('reports_avg_engagement', 'Avg Engagement')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Footer */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('reports_views_count', 'This report has been viewed {{count}} times', {
              count: report.view_count,
            })}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
            {t('reports_powered_by', 'Powered by PublishApps')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedReportPage;
